import React from "react";
import {
  Container,
  Typography,
  Divider,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Mosque,
  Restaurant,
  TravelExplore,
  CheckCircle,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const GreenDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.primary.light,
  borderWidth: 1,
  opacity: 0.5,
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  "& svg": {
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1),
    borderRadius: "50%",
    color: "#fff",
    marginRight: theme.spacing(2),
  },
}));

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: { xs: 8, md: 12 },
        mb: 6,
        px: { xs: 2, sm: 3 },
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          background: "linear-gradient(to bottom, #f5faf5, #ffffff)",
          border: "1px solid",
          borderColor: theme.palette.primary.light,
          boxShadow: "0 8px 32px rgba(46, 125, 50, 0.1)",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <Box textAlign="center" mb={6}>
          <Typography
            variant={isMobile ? "h3" : "h2"}
            gutterBottom
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.dark,
              mb: 2,
              fontFamily: "Poppins, sans-serif",
              position: "relative",
              "&:after": {
                content: '""',
                display: "block",
                width: "80px",
                height: "4px",
                backgroundColor: theme.palette.primary.main,
                margin: "16px auto 0",
                borderRadius: "2px",
              },
            }}
          >
            Welcome to Halal World
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              maxWidth: 800,
              mx: "auto",
              fontStyle: "italic",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            "Your trusted guide to halal living worldwide"
          </Typography>
        </Box>

        <Box mb={6}>
          <SectionHeader>
            <TravelExplore fontSize="large" />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.dark,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Our Mission
            </Typography>
          </SectionHeader>
          <Typography
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              fontSize: "1.1rem",
              color: theme.palette.text.primary,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            At{" "}
            <strong style={{ color: theme.palette.primary.dark }}>
              Halal World
            </strong>
            , we're dedicated to making <strong>halal travel</strong> accessible
            and enjoyable for Muslims everywhere. We believe that everyone
            should be able to explore the world's wonders while maintaining
            their <strong>Islamic values</strong> and dietary requirements.
          </Typography>
        </Box>

        <GreenDivider sx={{ my: 6 }} />

        {/* Services Section */}
        <Box mb={6}>
          <SectionHeader>
            <Restaurant fontSize="large" />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.dark,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Our Services
            </Typography>
          </SectionHeader>

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={4}
            mb={4}
          >
            <Box
              flex={1}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: "#f5faf5",
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  mb: 2,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Halal Dining Guide
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  lineHeight: 1.8,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Discover thousands of{" "}
                <strong>certified halal restaurants</strong> across the globe,
                complete with menus, photos, and authentic reviews from our
                Muslim community.
              </Typography>
            </Box>

            <Box
              flex={1}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: "#f5faf5",
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  mb: 2,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Mosque Locator
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  lineHeight: 1.8,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Find <strong>prayer spaces</strong> near you with our
                comprehensive mosque directory, featuring prayer times,
                facilities, and visitor information.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f5faf5",
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 600,
                mb: 2,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              E-Number Halal Checker
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{
                lineHeight: 1.8,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Our unique tool helps you verify food ingredients against{" "}
              <strong>halal certification standards</strong>, giving you peace
              of mind when shopping anywhere in the world.
            </Typography>
          </Box>
        </Box>

        <GreenDivider sx={{ my: 6 }} />

        <Box>
          <SectionHeader>
            <CheckCircle fontSize="large" />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.dark,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Our Vision
            </Typography>
          </SectionHeader>
          <Typography
            variant="body1"
            paragraph
            sx={{
              lineHeight: 1.8,
              fontSize: "1.1rem",
              color: theme.palette.text.primary,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            We envision becoming the{" "}
            <strong style={{ color: theme.palette.primary.dark }}>
              global standard
            </strong>{" "}
            for halal lifestyle resources, empowering Muslim travelers to
            navigate the world with <strong>confidence</strong> and{" "}
            <strong>ease</strong>. Through continuous innovation and community
            engagement, we aim to build bridges between cultures while
            preserving Islamic values.
          </Typography>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            mt: 6,
            "& svg": {
              fill: theme.palette.primary.light,
              opacity: 0.3,
              fontSize: "4rem",
            },
          }}
        >
          <Mosque />
        </Box>

        <Box mt={4} textAlign="center">
          <Typography
            variant="h6"
            sx={{
              fontStyle: "italic",
              color: theme.palette.primary.dark,
              maxWidth: 600,
              mx: "auto",
              fontWeight: 500,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            "Journey with confidence, guided by faith and Halal World"
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default About;
